import { format, fromUnixTime } from 'date-fns';

import { ResponseError } from 'services/api/base';
import { FileList, FileListEntry } from 'helper/products/fileViewer';

/*
 * Modelo de navegação do "Meus Arquivos".
 *
 * O `get-file-list` devolve a árvore inteira, mas com identidades instáveis: o `id` de pasta é um
 * `uuid1()` novo a cada request e o `id` de arquivo é um token Fernet (TTL de 4 h) que também muda.
 * Tudo que precisa sobreviver a um refetch (pasta aberta, favoritos, seleção) usa o CAMINHO de
 * rótulos como chave; o token só é lido na hora de baixar/enviar.
 */

export interface ExplorerFolder {
  kind: 'folder';
  /** Caminho de rótulos unido por `/` (raiz = ''). Estável entre requests. */
  key: string;
  name: string;
  path: string[];
  /** `name` cru do backend: token do diretório, exigido como `dir_token` no upload. */
  token: string;
  children: ExplorerNode[];
  /** Arquivos em toda a subárvore. */
  fileCount: number;
  totalSize: number;
  /** Modificação mais recente na subárvore (segundos Unix); `null` se não há arquivo. */
  latestModified: number | null;
  hasSubfolders: boolean;
}

export interface ExplorerFile {
  kind: 'file';
  /** Caminho da pasta + nome do arquivo. Estável entre requests. */
  key: string;
  name: string;
  /** Caminho da pasta que contém o arquivo. */
  path: string[];
  /** Token de download (`file-id`). Muda a cada request e expira. */
  id: string;
  size: number;
  /** Segundos Unix (rota legada: `new Date(ts * 1000)`). */
  modified: number;
  extension: string;
}

export type ExplorerNode = ExplorerFolder | ExplorerFile;

export type SearchScope = 'here' | 'all';
export type SortKey = 'name' | 'size' | 'date';
export type SortDir = 1 | -1;

export const ROOT_LABEL = 'ROOT';
/** `kind` especial dos chips: sem filtro. */
export const ALL_KINDS = 'all';
export const FOLDER_KIND = 'folder';

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
};

function buildFolder(
  name: string,
  token: string,
  path: string[],
  entries: FileListEntry[],
): ExplorerFolder {
  const children: ExplorerNode[] = entries.map((entry) => {
    if (entry.type === 'D') {
      return buildFolder(entry.label, entry.name, [...path, entry.label], entry.children);
    }
    return {
      kind: 'file',
      key: [...path, entry.name].join('/'),
      name: entry.name,
      path,
      id: entry.id,
      size: entry.size,
      modified: entry.modified,
      extension: extensionOf(entry.name),
    };
  });

  let fileCount = 0;
  let totalSize = 0;
  let latestModified: number | null = null;
  children.forEach((child) => {
    const childLatest = child.kind === 'folder' ? child.latestModified : child.modified;
    fileCount += child.kind === 'folder' ? child.fileCount : 1;
    totalSize += child.kind === 'folder' ? child.totalSize : child.size;
    if (childLatest !== null && (latestModified === null || childLatest > latestModified)) {
      latestModified = childLatest;
    }
  });

  return {
    kind: 'folder',
    key: path.join('/'),
    name,
    path,
    token,
    children,
    fileCount,
    totalSize,
    latestModified,
    hasSubfolders: children.some((child) => child.kind === 'folder'),
  };
}

export function buildExplorerTree(list: FileList): ExplorerFolder {
  return buildFolder(ROOT_LABEL, '', [], list);
}

export function findFolder(root: ExplorerFolder, path: string[]): ExplorerFolder | undefined {
  let current: ExplorerFolder | undefined = root;
  path.forEach((label) => {
    current = current?.children.find(
      (child): child is ExplorerFolder => child.kind === 'folder' && child.name === label,
    );
  });
  return current;
}

const flattened = new WeakMap<ExplorerFolder, ExplorerNode[]>();

/**
 * Todos os descendentes em pré-ordem (pastas incluídas, raiz excluída). Memorizado por árvore
 * (a busca global chama a cada tecla); quem usa não deve mutar o array devolvido.
 */
export function flattenTree(root: ExplorerFolder): ExplorerNode[] {
  const cached = flattened.get(root);
  if (cached) return cached;
  const out: ExplorerNode[] = [];
  const walk = (folder: ExplorerFolder) => {
    folder.children.forEach((child) => {
      out.push(child);
      if (child.kind === 'folder') walk(child);
    });
  };
  walk(root);
  flattened.set(root, out);
  return out;
}

/** Minúsculas e sem acento: "Diário" casa com "diario". */
export function normalizeSearchText(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Texto de busca de cada nó, normalizado uma vez só: a árvore da Ampere (que vê as pastas de
 * todos os clientes) passa de dezenas de milhares de nós e a busca roda a cada tecla. Os nós
 * não mudam depois de montados, então o cache por referência é seguro e some com a árvore.
 */
const searchTexts = new WeakMap<ExplorerNode, { name: string; fullPath: string }>();

function searchTextOf(node: ExplorerNode) {
  let text = searchTexts.get(node);
  if (!text) {
    // A chave já é o caminho completo (pasta: o próprio caminho; arquivo: pasta + nome).
    text = { name: normalizeSearchText(node.name), fullPath: normalizeSearchText(node.key) };
    searchTexts.set(node, text);
  }
  return text;
}

export function parseSearchTerms(query: string): string[] {
  return normalizeSearchText(query).split(/\s+/).filter(Boolean);
}

export function entryKind(node: ExplorerNode): string {
  return node.kind === 'folder' ? FOLDER_KIND : node.extension;
}

const nameCollator = new Intl.Collator('pt-BR', { numeric: true, sensitivity: 'base' });

const sortValue = (node: ExplorerNode, key: SortKey): number => {
  if (key === 'size') return node.kind === 'folder' ? node.fileCount : node.size;
  const date = node.kind === 'folder' ? node.latestModified : node.modified;
  return date ?? -Infinity;
};

/** Pastas sempre antes de arquivos; empate desempata pelo nome (A–Z). */
export function sortEntries(nodes: ExplorerNode[], key: SortKey, dir: SortDir): ExplorerNode[] {
  return [...nodes].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
    const byName = nameCollator.compare(a.name, b.name);
    if (key === 'name') return byName * dir;
    const diff = sortValue(a, key) - sortValue(b, key);
    return diff !== 0 ? Math.sign(diff) * dir : byName;
  });
}

export interface KindCount {
  kind: string;
  count: number;
}

export interface VisibleEntriesParams {
  root: ExplorerFolder;
  path: string[];
  query: string;
  scope: SearchScope;
  kind: string;
  sortKey: SortKey;
  sortDir: SortDir;
}

export interface VisibleEntries {
  entries: ExplorerNode[];
  /** Chips: "Todos", "Pastas" (se houver) e cada extensão, contados sobre o resultado da busca. */
  kinds: KindCount[];
  /** Busca em todas as pastas ativa: cada linha mostra o caminho. */
  isGlobal: boolean;
  terms: string[];
}

/**
 * O que a lista mostra. "Nesta pasta" filtra os filhos diretos pelo nome; "Todas as pastas"
 * (só com termo digitado) percorre a árvore inteira e casa o caminho + nome, então
 * "prevs diario" acha o que está em `-PREVS/diario`.
 */
export function selectVisibleEntries({
  root, path, query, scope, kind, sortKey, sortDir,
}: VisibleEntriesParams): VisibleEntries {
  const terms = parseSearchTerms(query);
  const isGlobal = scope === 'all' && terms.length > 0;
  const current = findFolder(root, path) ?? root;

  let matched = isGlobal ? flattenTree(root) : current.children;
  if (terms.length) {
    matched = matched.filter((node) => {
      const { name, fullPath } = searchTextOf(node);
      const haystack = isGlobal ? fullPath : name;
      return terms.every((term) => haystack.includes(term));
    });
  }

  const counts = new Map<string, number>();
  matched.forEach((node) => {
    const k = entryKind(node);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  });
  const extensions = Array.from(counts.keys()).filter((k) => k !== FOLDER_KIND).sort();
  const folderCount = counts.get(FOLDER_KIND);
  const kinds: KindCount[] = [
    { kind: ALL_KINDS, count: matched.length },
    ...(folderCount ? [{ kind: FOLDER_KIND, count: folderCount }] : []),
    ...extensions.map((k) => ({ kind: k, count: counts.get(k) as number })),
  ];

  const filtered = kind === ALL_KINDS
    ? matched
    : matched.filter((node) => entryKind(node) === kind);

  return {
    entries: sortEntries(filtered, sortKey, sortDir),
    kinds,
    isGlobal,
    terms,
  };
}

const FORBIDDEN_CHARS = /[/\\:*?"<>|]/;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/;
/** `index.file` é o marcador que o backend grava em cada pasta: nunca é listado nem enviado. */
const RESERVED_NAMES = new Set(['.', '..', 'index.file']);
export const FOLDER_NAME_MAX_LENGTH = 100;
export const FILE_NAME_MAX_LENGTH = 255;

/**
 * Regra de nome alinhada ao backend (`file_viewer_paths.nome_valido`): o que passa aqui não volta
 * como 422. Oculto (começa com ponto) não entra porque a listagem nunca o mostraria — ficaria
 * invisível e indeletável pela tela. Mensagem de erro, ou `null` se o nome é válido.
 */
function validateEntryName(rawName: string, maxLength: number): string | null {
  const name = rawName.trim();
  if (!name) return 'Informe um nome.';
  if (RESERVED_NAMES.has(name)) return 'Nome reservado.';
  if (name.startsWith('.')) return 'O nome não pode começar com ponto.';
  if (FORBIDDEN_CHARS.test(name)) return 'O nome não pode conter / \\ : * ? " < > |';
  if (CONTROL_CHARS.test(name)) return 'O nome não pode conter caracteres de controle.';
  if (name.length > maxLength) return `Use no máximo ${maxLength} caracteres.`;
  return null;
}

/** Mensagem de erro do nome da nova pasta, ou `null` se ele é válido. */
export function validateFolderName(rawName: string, siblingNames: string[]): string | null {
  const invalid = validateEntryName(rawName, FOLDER_NAME_MAX_LENGTH);
  if (invalid) return invalid;
  const lower = rawName.trim().toLocaleLowerCase('pt-BR');
  if (siblingNames.some((sibling) => sibling.toLocaleLowerCase('pt-BR') === lower)) {
    return 'Já existe uma pasta com esse nome aqui.';
  }
  return null;
}

/** Mensagem de erro do nome de um arquivo a enviar, ou `null` se o backend vai aceitá-lo. */
export function validateFileName(name: string): string | null {
  return validateEntryName(name, FILE_NAME_MAX_LENGTH);
}

const MB = 1024 * 1024;
/**
 * Limite por arquivo no upload — o mesmo do backend (`FILE_VIEWER_UPLOAD_MAX_MB`, 422
 * `arquivo_grande`). O arquivo vai em base64 dentro de um JSON, então o servidor segura o arquivo
 * inteiro na memória; acima disso a equipe sobe por outro caminho.
 */
export const UPLOAD_MAX_MB = 200;
export const UPLOAD_MAX_BYTES = UPLOAD_MAX_MB * MB;

const formatMb = (bytes: number) => (Math.ceil((bytes / MB) * 10) / 10)
  .toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Mensagem de erro do tamanho de um arquivo a enviar, ou `null` se cabe no limite. */
export function validateFileSize(sizeBytes: number, limitBytes = UPLOAD_MAX_BYTES): string | null {
  if (sizeBytes <= limitBytes) return null;
  return `Arquivo de ${formatMb(sizeBytes)} MB; o limite é ${Math.round(limitBytes / MB)} MB por arquivo.`;
}

/**
 * Nome livre para o upload (o backend recusa sobrescrever): `relatorio.pdf` →
 * `relatorio (1).pdf`. O índice entra antes da extensão para o arquivo continuar abrindo.
 */
export function uniqueFileName(name: string, takenNames: string[]): string {
  const taken = new Set(takenNames);
  if (!taken.has(name)) return name;
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  let index = 1;
  while (taken.has(`${base} (${index})${ext}`)) index += 1;
  return `${base} (${index})${ext}`;
}

/**
 * Caminho de cada arquivo dentro do .zip: abaixo do ancestral comum da seleção. Seleção de uma
 * pasta só sai "achatada"; seleção da busca global mantém as subpastas (e nomes nunca colidem).
 */
export function zipEntryNames(files: ExplorerFile[]): Map<string, string> {
  const common = files.length
    ? files.reduce((prefix, file) => {
      let i = 0;
      while (i < prefix.length && prefix[i] === file.path[i]) i += 1;
      return prefix.slice(0, i);
    }, files[0].path)
    : [];
  return new Map(files.map((file) => [
    file.key,
    [...file.path.slice(common.length), file.name].join('/'),
  ]));
}

export function zipFileName(path: string[], now: Date): string {
  const folder = path.length ? `_${path[path.length - 1]}` : '';
  return `meus-arquivos${folder}_${format(now, 'yyyyMMdd-HHmm')}.zip`;
}

/** Valor do `?pasta=` (rótulos de pasta nunca contêm `/`). */
export function encodeFolderParam(path: string[]): string {
  return path.join('/');
}

export function decodeFolderParam(param: string | null): string[] {
  return param ? param.split('/').filter(Boolean) : [];
}

/**
 * `file_viewer.token_expirado` do backend: code 403 com "Este link de download expirou…".
 * Não é falta de permissão — basta pedir a lista de novo para ganhar tokens novos.
 */
export function isExpiredTokenError(error: ResponseError | undefined): boolean {
  return Number(error?.status) === 403 && /expirou/i.test(String(error?.data ?? ''));
}

export type BadgeTone = 'sheet' | 'data' | 'code' | 'pdf' | 'archive' | 'doc' | 'image' | 'other';

const BADGE_TONES: Record<string, BadgeTone> = {
  xlsx: 'sheet',
  xls: 'sheet',
  xlsm: 'sheet',
  ods: 'sheet',
  csv: 'data',
  txt: 'data',
  dat: 'data',
  json: 'code',
  xml: 'code',
  pdf: 'pdf',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  gz: 'archive',
  docx: 'doc',
  doc: 'doc',
  pptx: 'doc',
  ppt: 'doc',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  svg: 'image',
};

export function fileBadgeTone(extension: string): BadgeTone {
  return BADGE_TONES[extension.toLowerCase()] ?? 'other';
}

/** Data de modificação (segundos Unix, rota legada); `—` para pasta sem arquivos. */
export function formatModified(seconds: number | null, withTime = true): string {
  if (seconds === null) return '—';
  return format(fromUnixTime(seconds), withTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy');
}

export const pluralize = (count: number, singular: string, plural: string) => (
  `${count.toLocaleString('pt-BR')} ${count === 1 ? singular : plural}`
);
